import { useState } from "react";

const API_BASE = "http://localhost:8000";

// ─── Constants ────────────────────────────────────────────────────────────────

const FRAMEWORKS = [
  { id: "glba",    name: "GLBA Safeguards Rule",  desc: "Required for all financial institutions handling customer data", required: true,  color: "#3b82f6" },
  { id: "pcidss",  name: "PCI-DSS v4.0",           desc: "Required if you process, store, or transmit payment card data", required: false, color: "#f59e0b" },
  { id: "sox",     name: "SOX ITGC",               desc: "Required for publicly traded companies or their subsidiaries",  required: false, color: "#ef4444" },
  { id: "nist",    name: "NIST 800-53 Rev 5",      desc: "Federal standard, widely adopted as a security baseline",       required: false, color: "#22c55e" },
  { id: "ffiec",   name: "FFIEC CAT",              desc: "Recommended for banks and credit unions",                        required: false, color: "#a78bfa" },
];

const INSTITUTION_TYPES = [
  "Community Bank", "Credit Union", "Regional Bank", "National Bank",
  "Investment Firm", "Insurance Company", "Payment Processor",
  "Mortgage Lender", "Fintech", "Other Financial Institution",
];

const ASSESSMENT_CATEGORIES = [
  { key: "identity_access",          label: "Identity & Access Management" },
  { key: "network_security",         label: "Network Security" },
  { key: "data_protection",          label: "Data Protection" },
  { key: "incident_response",        label: "Incident Response" },
  { key: "vulnerability_management", label: "Vulnerability Management" },
  { key: "cloud_security",           label: "Cloud Security" },
  { key: "compliance_governance",    label: "Compliance & Governance" },
  { key: "logging_monitoring",       label: "Logging & Monitoring" },
];

const MATURITY_LABELS = ["Not Implemented", "Initial", "Developing", "Defined", "Managed", "Optimizing"];

// ─── Step definitions ─────────────────────────────────────────────────────────

const STEPS = [
  { id: "profile",     label: "Company Profile",     icon: "🏦", desc: "Tell us about your organization" },
  { id: "frameworks",  label: "Compliance Scope",    icon: "📋", desc: "Select your regulatory frameworks" },
  { id: "credentials", label: "Integrations",        icon: "🔌", desc: "Connect your security tools" },
  { id: "assessment",  label: "Initial Assessment",  icon: "📊", desc: "Rate your current security maturity" },
];

// ─── Progress Bar ─────────────────────────────────────────────────────────────

function StepIndicator({ currentStep }) {
  return (
    <div style={sty.stepIndicator}>
      {STEPS.map((step, i) => {
        const done    = i < currentStep;
        const active  = i === currentStep;
        return (
          <div key={step.id} style={sty.stepItem}>
            <div style={{
              ...sty.stepCircle,
              ...(done   ? sty.stepCircleDone   : {}),
              ...(active ? sty.stepCircleActive : {}),
            }}>
              {done ? "✓" : step.icon}
            </div>
            <div style={sty.stepMeta}>
              <div style={{ ...sty.stepLabel, color: active ? "#f1f5f9" : done ? "#64748b" : "#334155" }}>
                {step.label}
              </div>
              <div style={sty.stepDesc}>{step.desc}</div>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ ...sty.stepConnector, background: done ? "#22c55e40" : "#1f2937" }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1: Company Profile ──────────────────────────────────────────────────

function StepProfile({ data, onChange }) {
  return (
    <div style={sty.stepContent}>
      <div style={sty.stepHeading}>Tell us about your organization</div>
      <div style={sty.stepSub}>This information personalizes your risk assessments and compliance reports.</div>

      <div style={sty.fieldGrid}>
        <div style={{ ...sty.field, gridColumn: "1 / -1" }}>
          <label style={sty.label}>Organization Name *</label>
          <input style={sty.input}
            value={data.org_name || ""}
            onChange={e => onChange("org_name", e.target.value)}
            placeholder="Acme Community Bank" />
        </div>

        <div style={sty.field}>
          <label style={sty.label}>Institution Type *</label>
          <select style={sty.select}
            value={data.institution_type || ""}
            onChange={e => onChange("institution_type", e.target.value)}>
            <option value="">Select type...</option>
            {INSTITUTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div style={sty.field}>
          <label style={sty.label}>Number of Employees</label>
          <select style={sty.select}
            value={data.employee_count || ""}
            onChange={e => onChange("employee_count", e.target.value)}>
            <option value="">Select range...</option>
            {["1–50","51–200","201–500","501–1,000","1,001–5,000","5,000+"].map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        <div style={sty.field}>
          <label style={sty.label}>Primary Contact Name *</label>
          <input style={sty.input}
            value={data.contact_name || ""}
            onChange={e => onChange("contact_name", e.target.value)}
            placeholder="Jane Smith" />
        </div>

        <div style={sty.field}>
          <label style={sty.label}>Primary Contact Email *</label>
          <input style={sty.input} type="email"
            value={data.contact_email || ""}
            onChange={e => onChange("contact_email", e.target.value)}
            placeholder="jane@acmebank.com" />
        </div>

        <div style={sty.field}>
          <label style={sty.label}>Title / Role</label>
          <input style={sty.input}
            value={data.contact_title || ""}
            onChange={e => onChange("contact_title", e.target.value)}
            placeholder="Chief Information Security Officer" />
        </div>

        <div style={sty.field}>
          <label style={sty.label}>Primary Regulator</label>
          <select style={sty.select}
            value={data.regulator || ""}
            onChange={e => onChange("regulator", e.target.value)}>
            <option value="">Select...</option>
            {["OCC","FDIC","Federal Reserve","NCUA","CFPB","State Regulator","Multiple","Other"].map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        <div style={sty.field}>
          <label style={sty.label}>Annual Revenue Range</label>
          <select style={sty.select}
            value={data.revenue_range || ""}
            onChange={e => onChange("revenue_range", e.target.value)}>
            <option value="">Select range...</option>
            {["Under $10M","$10M–$50M","$50M–$250M","$250M–$1B","Over $1B"].map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

// ─── Step 2: Framework Selection ──────────────────────────────────────────────

function StepFrameworks({ data, onChange }) {
  const selected = data.frameworks || ["glba"];

  const toggle = (id) => {
    if (id === "glba") return; // GLBA always required
    const next = selected.includes(id)
      ? selected.filter(f => f !== id)
      : [...selected, id];
    onChange("frameworks", next);
  };

  return (
    <div style={sty.stepContent}>
      <div style={sty.stepHeading}>Select your compliance frameworks</div>
      <div style={sty.stepSub}>
        GLBA is required for all financial institutions. Select any additional frameworks that apply to your organization.
      </div>

      <div style={sty.fwGrid}>
        {FRAMEWORKS.map(fw => {
          const isSelected = selected.includes(fw.id);
          return (
            <div key={fw.id}
              style={{
                ...sty.fwCard,
                ...(isSelected ? { ...sty.fwCardSelected, borderColor: fw.color + "60", background: fw.color + "08" } : {}),
                ...(fw.required ? { cursor: "default" } : { cursor: "pointer" }),
              }}
              onClick={() => toggle(fw.id)}
            >
              <div style={sty.fwCardTop}>
                <div style={{ ...sty.fwCheck,
                  background: isSelected ? fw.color : "transparent",
                  borderColor: isSelected ? fw.color : "#1f2937",
                }}>
                  {isSelected && <span style={{ color: "#fff", fontSize: 11, fontWeight: 700 }}>✓</span>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={sty.fwName}>{fw.name}</div>
                  {fw.required && (
                    <span style={{ ...sty.fwRequired, color: fw.color }}>Required</span>
                  )}
                </div>
              </div>
              <div style={sty.fwDesc}>{fw.desc}</div>
            </div>
          );
        })}
      </div>

      <div style={sty.fwNote}>
        <span style={sty.fwNoteIcon}>ℹ</span>
        <span>You can add or remove frameworks at any time from your Settings. Your compliance reports and control mappings will update automatically.</span>
      </div>
    </div>
  );
}

// ─── Step 3: Credentials ──────────────────────────────────────────────────────

function StepCredentials({ data, onChange }) {
  const [awsExpanded,    setAwsExpanded]    = useState(false);
  const [qualysExpanded, setQualysExpanded] = useState(false);
  const [showPass,       setShowPass]       = useState({});

  const awsCreds    = data.aws_credentials    || {};
  const qualysCreds = data.qualys_credentials || {};

  const setAws    = (k, v) => onChange("aws_credentials",    { ...awsCreds,    [k]: v });
  const setQualys = (k, v) => onChange("qualys_credentials", { ...qualysCreds, [k]: v });

  const awsFilled    = awsCreds.access_key_id && awsCreds.secret_access_key && awsCreds.region;
  const qualysFilled = qualysCreds.username && qualysCreds.password && qualysCreds.platform_url;

  return (
    <div style={sty.stepContent}>
      <div style={sty.stepHeading}>Connect your security tools</div>
      <div style={sty.stepSub}>
        Integrations enable automated data collection. You can skip this step and configure integrations later in Settings.
      </div>

      <div style={sty.credCards}>
        {/* AWS */}
        <div style={{ ...sty.credCard, ...(awsFilled ? sty.credCardConnected : {}) }}>
          <div style={sty.credCardHeader} onClick={() => setAwsExpanded(v => !v)}>
            <div style={sty.credCardLeft}>
              <span style={{ ...sty.credLogo, color: "#f97316", background: "#f9731615" }}>☁</span>
              <div>
                <div style={sty.credName}>Amazon Web Services</div>
                <div style={sty.credStatus}>
                  {awsFilled
                    ? <span style={{ color: "#22c55e" }}>✓ Credentials entered</span>
                    : <span style={{ color: "#475569" }}>Not configured</span>}
                </div>
              </div>
            </div>
            <div style={sty.credToggle}>{awsExpanded ? "▲" : "▼"}</div>
          </div>

          {awsExpanded && (
            <div style={sty.credForm}>
              <div style={sty.credNote}>🔒 Read-only SecurityAudit policy required. Credentials encrypted at rest.</div>
              <div style={sty.fieldGrid}>
                <div style={sty.field}>
                  <label style={sty.label}>Access Key ID</label>
                  <input style={sty.input} value={awsCreds.access_key_id || ""}
                    onChange={e => setAws("access_key_id", e.target.value)}
                    placeholder="AKIA..." autoComplete="off" spellCheck={false} />
                </div>
                <div style={sty.field}>
                  <label style={sty.label}>Secret Access Key</label>
                  <div style={{ position:"relative" }}>
                    <input style={sty.input}
                      type={showPass.aws ? "text" : "password"}
                      value={awsCreds.secret_access_key || ""}
                      onChange={e => setAws("secret_access_key", e.target.value)}
                      placeholder="••••••••••••••••" autoComplete="new-password" />
                    <button style={sty.eyeBtn} onClick={() => setShowPass(p => ({ ...p, aws: !p.aws }))}>
                      {showPass.aws ? "🙈" : "👁"}
                    </button>
                  </div>
                </div>
                <div style={sty.field}>
                  <label style={sty.label}>Primary Region</label>
                  <select style={sty.select} value={awsCreds.region || ""}
                    onChange={e => setAws("region", e.target.value)}>
                    <option value="">Select region...</option>
                    {["us-east-1","us-east-2","us-west-1","us-west-2","eu-west-1","eu-central-1","ap-southeast-1","ap-northeast-1"].map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Qualys */}
        <div style={{ ...sty.credCard, ...(qualysFilled ? sty.credCardConnected : {}) }}>
          <div style={sty.credCardHeader} onClick={() => setQualysExpanded(v => !v)}>
            <div style={sty.credCardLeft}>
              <span style={{ ...sty.credLogo, color: "#ef4444", background: "#ef444415" }}>🔍</span>
              <div>
                <div style={sty.credName}>Qualys VMDR</div>
                <div style={sty.credStatus}>
                  {qualysFilled
                    ? <span style={{ color: "#22c55e" }}>✓ Credentials entered</span>
                    : <span style={{ color: "#475569" }}>Not configured</span>}
                </div>
              </div>
            </div>
            <div style={sty.credToggle}>{qualysExpanded ? "▲" : "▼"}</div>
          </div>

          {qualysExpanded && (
            <div style={sty.credForm}>
              <div style={sty.credNote}>🔒 Reader role API user required. Credentials encrypted at rest.</div>
              <div style={sty.fieldGrid}>
                <div style={sty.field}>
                  <label style={sty.label}>API Username</label>
                  <input style={sty.input} value={qualysCreds.username || ""}
                    onChange={e => setQualys("username", e.target.value)}
                    placeholder="api_reader@company.com" autoComplete="off" />
                </div>
                <div style={sty.field}>
                  <label style={sty.label}>API Password</label>
                  <div style={{ position:"relative" }}>
                    <input style={sty.input}
                      type={showPass.qualys ? "text" : "password"}
                      value={qualysCreds.password || ""}
                      onChange={e => setQualys("password", e.target.value)}
                      placeholder="••••••••••••••••" autoComplete="new-password" />
                    <button style={sty.eyeBtn} onClick={() => setShowPass(p => ({ ...p, qualys: !p.qualys }))}>
                      {showPass.qualys ? "🙈" : "👁"}
                    </button>
                  </div>
                </div>
                <div style={{ ...sty.field, gridColumn: "1 / -1" }}>
                  <label style={sty.label}>Platform URL</label>
                  <input style={sty.input} value={qualysCreds.platform_url || ""}
                    onChange={e => setQualys("platform_url", e.target.value)}
                    placeholder="qualysapi.qualys.com" autoComplete="off" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Skip notice */}
        <div style={sty.skipNotice}>
          <span style={sty.skipIcon}>💡</span>
          <span>Integrations are optional during onboarding. You can configure them anytime from Settings → Integrations.</span>
        </div>
      </div>
    </div>
  );
}

// ─── Step 4: Initial Assessment ───────────────────────────────────────────────

function StepAssessment({ data, onChange }) {
  const scores = data.assessment_scores || {};

  const setScore = (key, val) => {
    onChange("assessment_scores", { ...scores, [key]: { score: val, target: 4 } });
  };

  return (
    <div style={sty.stepContent}>
      <div style={sty.stepHeading}>Rate your current security maturity</div>
      <div style={sty.stepSub}>
        Select your current maturity level for each security domain. Be honest — this becomes your baseline for tracking improvement over time.
      </div>

      <div style={sty.assessGrid}>
        {ASSESSMENT_CATEGORIES.map(cat => {
          const current = scores[cat.key]?.score ?? null;
          return (
            <div key={cat.key} style={sty.assessRow}>
              <div style={sty.assessLabel}>{cat.label}</div>
              <div style={sty.assessButtons}>
                {[0, 1, 2, 3, 4, 5].map(level => (
                  <button key={level}
                    title={MATURITY_LABELS[level]}
                    style={{
                      ...sty.assessBtn,
                      ...(current === level ? sty.assessBtnActive : {}),
                      ...(current !== null && level < current ? sty.assessBtnPast : {}),
                    }}
                    onClick={() => setScore(cat.key, level)}
                  >
                    {level}
                  </button>
                ))}
                <span style={sty.assessScoreLabel}>
                  {current !== null ? MATURITY_LABELS[current] : "Not rated"}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={sty.assessNote}>
        <span style={sty.assessNoteIcon}>📖</span>
        <div>
          <strong style={{ color: "#94a3b8" }}>Maturity Scale:</strong>
          <span style={{ color: "#64748b" }}> 0 = Not Implemented · 1 = Initial · 2 = Developing · 3 = Defined · 4 = Managed · 5 = Optimizing</span>
        </div>
      </div>
    </div>
  );
}

// ─── Completion Screen ────────────────────────────────────────────────────────

function CompletionScreen({ data, onGoToDashboard }) {
  return (
    <div style={sty.completionWrap}>
      <div style={sty.completionIcon}>🎉</div>
      <h2 style={sty.completionTitle}>You're all set, {data.contact_name?.split(" ")[0] || "there"}!</h2>
      <p style={sty.completionSub}>
        {data.org_name} is now configured in CISO Advisor. Your initial risk assessment has been saved and your compliance frameworks are loaded.
      </p>
      <div style={sty.completionCards}>
        {[
          { icon: "📊", title: "Risk Assessment", desc: "Your maturity scores have been saved as your baseline." },
          { icon: "📋", title: "Compliance Scope", desc: `${(data.frameworks||[]).length} framework(s) configured for reporting.` },
          { icon: "🔌", title: "Integrations", desc: data.aws_credentials?.access_key_id || data.qualys_credentials?.username ? "Credentials saved — collection jobs ready." : "Configure anytime in Settings → Integrations." },
          { icon: "🛡", title: "Next Step", desc: "Run your first MCMC risk calculation to get your Expected Annual Loss figure." },
        ].map((c, i) => (
          <div key={i} style={sty.completionCard}>
            <span style={sty.completionCardIcon}>{c.icon}</span>
            <div>
              <div style={sty.completionCardTitle}>{c.title}</div>
              <div style={sty.completionCardDesc}>{c.desc}</div>
            </div>
          </div>
        ))}
      </div>
      <button style={sty.dashboardBtn} onClick={onGoToDashboard}>
        Go to Dashboard →
      </button>
    </div>
  );
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────

export default function OnboardingWizard() {
  const [step, setStep]       = useState(0);
  const [data, setData]       = useState({ frameworks: ["glba"] });
  const [saving, setSaving]   = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError]     = useState(null);

  const setField = (key, val) => setData(prev => ({ ...prev, [key]: val }));

  const canAdvance = () => {
    if (step === 0) return !!(data.org_name?.trim() && data.institution_type && data.contact_name?.trim() && data.contact_email?.trim());
    if (step === 1) return (data.frameworks || []).length > 0;
    if (step === 2) return true; // credentials optional
    if (step === 3) return Object.keys(data.assessment_scores || {}).length >= 4;
    return true;
  };

  const handleNext = async () => {
    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
      return;
    }
    // Final step — save everything
    setSaving(true);
    setError(null);
    try {
      const customerId = `cust_${data.org_name.toLowerCase().replace(/[^a-z0-9]/g, "_")}_${Date.now().toString().slice(-6)}`;
      localStorage.setItem("customer_id", customerId);
      localStorage.setItem("org_name", data.org_name);

      // Save customer profile
      await fetch(`${API_BASE}/api/customers/onboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_id: customerId,
          org_name: data.org_name,
          institution_type: data.institution_type,
          employee_count: data.employee_count,
          contact_name: data.contact_name,
          contact_email: data.contact_email,
          contact_title: data.contact_title,
          regulator: data.regulator,
          revenue_range: data.revenue_range,
          frameworks: data.frameworks,
        }),
      }).catch(() => {}); // graceful — backend endpoint to be built

      // Save assessment scores
      if (data.assessment_scores && Object.keys(data.assessment_scores).length > 0) {
        await fetch(`${API_BASE}/api/assessments/initialize`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            customer_id: customerId,
            responses: data.assessment_scores,
            organization_name: data.org_name,
          }),
        }).catch(() => {});
      }

      // Save credentials
      if (data.aws_credentials?.access_key_id) {
        await fetch(`${API_BASE}/api/credentials/aws/save`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ credentials: data.aws_credentials }),
        }).catch(() => {});
      }
      if (data.qualys_credentials?.username) {
        await fetch(`${API_BASE}/api/credentials/qualys/save`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ credentials: data.qualys_credentials }),
        }).catch(() => {});
      }

      setComplete(true);
    } catch (e) {
      setError("Failed to save onboarding data. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => setStep(s => Math.max(0, s - 1));

  const handleGoToDashboard = () => {
    window.location.href = "/";
  };

  if (complete) {
    return (
      <div style={sty.page}>
        <div style={sty.bgGradient} />
        <CompletionScreen data={data} onGoToDashboard={handleGoToDashboard} />
      </div>
    );
  }

  return (
    <div style={sty.page}>
      <div style={sty.bgGradient} />

      {/* Logo bar */}
      <div style={sty.logoBar}>
        <div style={sty.logo}>
          <span style={sty.logoIcon}>🛡</span>
          <span style={sty.logoText}>CISO Advisor</span>
        </div>
        <div style={sty.logoSub}>by Moyer Scientific Research</div>
      </div>

      <div style={sty.layout}>
        {/* Left rail — step indicator */}
        <div style={sty.leftRail}>
          <div style={sty.leftRailTop}>
            <h1 style={sty.wizardTitle}>Welcome aboard.</h1>
            <p style={sty.wizardSub}>Let's get your organization configured in about 5 minutes.</p>
          </div>
          <StepIndicator currentStep={step} />
          <div style={sty.leftRailBottom}>
            <div style={sty.securityNote}>
              <span>🔒</span>
              <span>All data is encrypted at rest using AES-256. Credentials are never stored in plain text.</span>
            </div>
          </div>
        </div>

        {/* Right panel — step content */}
        <div style={sty.rightPanel}>
          <div style={sty.panelHeader}>
            <div style={sty.panelStep}>Step {step + 1} of {STEPS.length}</div>
            <div style={sty.panelProgress}>
              <div style={{ ...sty.panelProgressFill, width: `${((step + 1) / STEPS.length) * 100}%` }} />
            </div>
          </div>

          <div style={sty.panelBody}>
            {step === 0 && <StepProfile     data={data} onChange={setField} />}
            {step === 1 && <StepFrameworks  data={data} onChange={setField} />}
            {step === 2 && <StepCredentials data={data} onChange={setField} />}
            {step === 3 && <StepAssessment  data={data} onChange={setField} />}
          </div>

          {error && <div style={sty.errorBanner}>⚠ {error}</div>}

          <div style={sty.panelFooter}>
            {step > 0 && (
              <button style={sty.backBtn} onClick={handleBack} disabled={saving}>
                ← Back
              </button>
            )}
            <div style={{ flex: 1 }} />
            {step === 2 && (
              <button style={sty.skipBtn} onClick={() => setStep(3)}>
                Skip for now
              </button>
            )}
            <button
              style={{
                ...sty.nextBtn,
                opacity: (!canAdvance() || saving) ? 0.45 : 1,
                cursor: (!canAdvance() || saving) ? "not-allowed" : "pointer",
              }}
              onClick={handleNext}
              disabled={!canAdvance() || saving}
            >
              {saving ? "Saving..." : step === STEPS.length - 1 ? "Complete Setup →" : "Continue →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const sty = {
  page: {
    minHeight: "100vh", background: "#060911",
    color: "#e2e8f0", position: "relative", overflow: "hidden",
    fontFamily: "'DM Sans', system-ui, sans-serif",
  },
  bgGradient: {
    position: "absolute", inset: 0, pointerEvents: "none",
    background: "radial-gradient(ellipse 80% 60% at 60% 0%, #0f2040 0%, transparent 70%), radial-gradient(ellipse 40% 40% at 10% 80%, #0a1a2e 0%, transparent 60%)",
  },
  logoBar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "20px 40px", position: "relative", zIndex: 1,
    borderBottom: "1px solid #111827",
  },
  logo: { display: "flex", alignItems: "center", gap: 10 },
  logoIcon: { fontSize: 22 },
  logoText: { fontSize: 18, fontWeight: 800, color: "#f1f5f9", letterSpacing: "-0.02em" },
  logoSub: { fontSize: 11, color: "#334155", fontFamily: "'DM Mono', monospace" },
  layout: {
    display: "flex", minHeight: "calc(100vh - 65px)",
    position: "relative", zIndex: 1,
  },
  leftRail: {
    width: 320, flexShrink: 0,
    borderRight: "1px solid #111827",
    padding: "40px 32px",
    display: "flex", flexDirection: "column", gap: 0,
    background: "#04060d",
  },
  leftRailTop: { marginBottom: 40 },
  wizardTitle: {
    fontSize: 28, fontWeight: 800, color: "#f1f5f9",
    margin: "0 0 10px", letterSpacing: "-0.03em",
  },
  wizardSub: { fontSize: 13, color: "#475569", lineHeight: 1.6, margin: 0 },
  leftRailBottom: { marginTop: "auto", paddingTop: 32 },
  securityNote: {
    display: "flex", gap: 8, alignItems: "flex-start",
    fontSize: 11, color: "#334155", lineHeight: 1.6,
  },
  stepIndicator: { display: "flex", flexDirection: "column", gap: 0 },
  stepItem: { display: "flex", alignItems: "flex-start", gap: 12, position: "relative" },
  stepCircle: {
    width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
    background: "#0f1420", border: "2px solid #1f2937",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 16, color: "#334155",
  },
  stepCircleActive: { border: "2px solid #3b82f6", background: "#0f2040", color: "#93c5fd" },
  stepCircleDone:  { border: "2px solid #22c55e40", background: "#0c1a0e", color: "#22c55e", fontSize: 14, fontWeight: 700 },
  stepMeta: { paddingBottom: 28, flex: 1 },
  stepLabel: { fontSize: 13, fontWeight: 600, marginBottom: 2 },
  stepDesc:  { fontSize: 11, color: "#334155" },
  stepConnector: {
    position: "absolute", left: 17, top: 36,
    width: 2, height: 28,
  },
  rightPanel: {
    flex: 1, display: "flex", flexDirection: "column",
    maxWidth: 720, padding: "40px 48px",
  },
  panelHeader: { marginBottom: 8 },
  panelStep: { fontSize: 11, color: "#334155", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8, fontFamily: "'DM Mono', monospace" },
  panelProgress: { height: 3, background: "#111827", borderRadius: 2, overflow: "hidden" },
  panelProgressFill: { height: "100%", background: "linear-gradient(90deg, #3b82f6, #22c55e)", borderRadius: 2, transition: "width 0.4s ease" },
  panelBody: { flex: 1, paddingTop: 8 },
  panelFooter: {
    display: "flex", alignItems: "center", gap: 10,
    paddingTop: 24, borderTop: "1px solid #111827", marginTop: 24,
  },
  // Step content
  stepContent: { display: "flex", flexDirection: "column", gap: 20 },
  stepHeading: { fontSize: 22, fontWeight: 800, color: "#f1f5f9", letterSpacing: "-0.02em" },
  stepSub: { fontSize: 13, color: "#64748b", lineHeight: 1.6, marginTop: -12 },
  // Fields
  fieldGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
  field: { display: "flex", flexDirection: "column", gap: 5 },
  label: { fontSize: 10, fontWeight: 700, color: "#475569", letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'DM Mono', monospace" },
  input: {
    background: "#0d1117", border: "1px solid #1f2937",
    borderRadius: 8, padding: "10px 12px",
    color: "#e2e8f0", fontSize: 13, outline: "none",
    fontFamily: "inherit",
  },
  select: {
    background: "#0d1117", border: "1px solid #1f2937",
    borderRadius: 8, padding: "10px 12px",
    color: "#e2e8f0", fontSize: 13, outline: "none",
    fontFamily: "inherit",
  },
  eyeBtn: {
    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
    background: "none", border: "none", cursor: "pointer", fontSize: 14,
  },
  // Frameworks
  fwGrid: { display: "flex", flexDirection: "column", gap: 10 },
  fwCard: {
    background: "#0c0f1a", border: "1px solid #1f2937",
    borderRadius: 10, padding: "14px 16px",
    transition: "all 0.15s",
  },
  fwCardSelected: { background: "#0d1220" },
  fwCardTop: { display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 6 },
  fwCheck: {
    width: 20, height: 20, borderRadius: 5, border: "2px solid",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, marginTop: 1, transition: "all 0.15s",
  },
  fwName: { fontSize: 14, fontWeight: 600, color: "#f1f5f9" },
  fwRequired: { fontSize: 10, fontWeight: 700, letterSpacing: "0.08em" },
  fwDesc: { fontSize: 12, color: "#475569", lineHeight: 1.5, paddingLeft: 32 },
  fwNote: {
    display: "flex", gap: 8, alignItems: "flex-start",
    background: "#0a0d14", border: "1px solid #1f2937",
    borderRadius: 8, padding: "12px 14px",
    fontSize: 12, color: "#475569", lineHeight: 1.6,
  },
  fwNoteIcon: { fontSize: 14, flexShrink: 0 },
  // Credentials
  credCards: { display: "flex", flexDirection: "column", gap: 12 },
  credCard: {
    background: "#0c0f1a", border: "1px solid #1f2937",
    borderRadius: 10, overflow: "hidden",
  },
  credCardConnected: { borderColor: "#22c55e30" },
  credCardHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "16px 18px", cursor: "pointer",
  },
  credCardLeft: { display: "flex", gap: 12, alignItems: "center" },
  credLogo: { width: 38, height: 38, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 },
  credName: { fontSize: 14, fontWeight: 600, color: "#f1f5f9" },
  credStatus: { fontSize: 12, marginTop: 2 },
  credToggle: { fontSize: 12, color: "#475569" },
  credForm: { padding: "0 18px 18px", borderTop: "1px solid #111827" },
  credNote: {
    fontSize: 11, color: "#475569",
    background: "#080b12", borderRadius: 6,
    padding: "8px 10px", margin: "12px 0",
  },
  skipNotice: {
    display: "flex", gap: 8, alignItems: "flex-start",
    fontSize: 12, color: "#334155", lineHeight: 1.6,
    padding: "4px 0",
  },
  skipIcon: { flexShrink: 0 },
  // Assessment
  assessGrid: { display: "flex", flexDirection: "column", gap: 14 },
  assessRow: { display: "flex", alignItems: "center", gap: 16 },
  assessLabel: { fontSize: 12, color: "#94a3b8", width: 200, flexShrink: 0 },
  assessButtons: { display: "flex", alignItems: "center", gap: 4, flex: 1 },
  assessBtn: {
    width: 34, height: 34, borderRadius: 7,
    background: "#0d1117", border: "1px solid #1f2937",
    color: "#475569", fontSize: 13, fontWeight: 700,
    cursor: "pointer", transition: "all 0.12s",
    fontFamily: "'DM Mono', monospace",
  },
  assessBtnActive: { background: "#1e3a5f", border: "1px solid #3b82f660", color: "#93c5fd" },
  assessBtnPast: { background: "#111827", border: "1px solid #22c55e30", color: "#22c55e" },
  assessScoreLabel: { fontSize: 11, color: "#334155", marginLeft: 8, minWidth: 100 },
  assessNote: {
    display: "flex", gap: 8, alignItems: "flex-start",
    background: "#0a0d14", border: "1px solid #1f2937",
    borderRadius: 8, padding: "12px 14px",
    fontSize: 12, color: "#475569", lineHeight: 1.6,
  },
  assessNoteIcon: { flexShrink: 0 },
  // Buttons
  backBtn: {
    padding: "10px 18px", background: "none",
    border: "1px solid #1f2937", borderRadius: 8,
    color: "#64748b", fontSize: 13, cursor: "pointer",
  },
  skipBtn: {
    padding: "10px 18px", background: "none",
    border: "none", color: "#334155",
    fontSize: 13, cursor: "pointer",
  },
  nextBtn: {
    padding: "11px 24px",
    background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
    border: "none", borderRadius: 9,
    color: "#fff", fontSize: 14, fontWeight: 700,
    boxShadow: "0 4px 16px #1d4ed820",
  },
  errorBanner: {
    background: "#1a0c0c", border: "1px solid #ef444330",
    borderRadius: 8, padding: "10px 14px",
    fontSize: 13, color: "#ef4444", margin: "0 0 12px",
  },
  // Completion
  completionWrap: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", minHeight: "calc(100vh - 65px)",
    padding: "40px", position: "relative", zIndex: 1, gap: 20,
    textAlign: "center",
  },
  completionIcon: { fontSize: 56, marginBottom: 8 },
  completionTitle: { fontSize: 32, fontWeight: 800, color: "#f1f5f9", margin: 0, letterSpacing: "-0.03em" },
  completionSub: { fontSize: 15, color: "#64748b", maxWidth: 480, lineHeight: 1.7, margin: 0 },
  completionCards: {
    display: "grid", gridTemplateColumns: "1fr 1fr",
    gap: 14, width: "100%", maxWidth: 560, marginTop: 8,
  },
  completionCard: {
    display: "flex", gap: 12, alignItems: "flex-start",
    background: "#0c0f1a", border: "1px solid #111827",
    borderRadius: 10, padding: "14px 16px", textAlign: "left",
  },
  completionCardIcon: { fontSize: 20, flexShrink: 0 },
  completionCardTitle: { fontSize: 13, fontWeight: 700, color: "#f1f5f9", marginBottom: 4 },
  completionCardDesc: { fontSize: 12, color: "#475569", lineHeight: 1.5 },
  dashboardBtn: {
    padding: "13px 32px",
    background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
    border: "none", borderRadius: 10,
    color: "#fff", fontSize: 15, fontWeight: 700,
    cursor: "pointer", marginTop: 8,
    boxShadow: "0 6px 20px #1d4ed830",
  },
};

